import { VStack, Stack, HStack, Container, useDisclosure } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCheckTrialEligibilityQuery } from 'hooks/queries/billing/useCheckTrialEligibilityQuery';
import { useGetPriceQuery } from 'hooks/queries/billing/useGetPrice';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useEffect, useState } from 'react';

import { useAuth } from 'auth/AuthProvider';
import { useCurrentUser } from 'auth/CurrentUserProvider';
import BigTokens from 'components/BigTokens';
import Button from 'components/Button';
import { FeaturesList } from 'components/FeaturesList';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { TokensModal } from 'components/Modal/TokensModal';
import PageLoader from 'components/PageLoader';
import { SEO } from 'components/SEO';
import Text from 'components/Text';
import { DATE_FORMAT, plans } from 'config/constants/plans';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { EdgeFunctionName, SubscribeParams, UpdatePricingCounterParams } from 'types/edgeFunctions';
import { ERoute } from 'types/frontend';
import { getMetaTrackingData } from 'utils/metaTracking';

import { FAQ } from './FAQ';
import PlanCheckout from './PlanCheckout';

const Pricing = () => {
  const { userId } = useAuth();
  const supabase = useSupabaseTypedClient();
  const { toastError } = useToastWrapper();

  const isSignedIn = !!userId;

  const mutation = useMutation<boolean, string, UpdatePricingCounterParams>({
    mutationFn: supabaseMutationFn(({ count }) =>
      supabase.functions.invoke<boolean>(EdgeFunctionName.UpdatePricingCounter, {
        body: { count } as UpdatePricingCounterParams,
      })
    ),
    onError: () => {
      // silent error updating under the hood
    },
  });

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    supabase.functions.invoke(EdgeFunctionName.UpdatePricingCounter, {
      body: { count: 1 } as UpdatePricingCounterParams,
    });
  }, []);

  useScrollToTop();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, isPending } = useGetPriceQuery();
  const [isPPPActive, setIsPPPActive] = useState(false);
  const [processingPlan, setProcessingPlan] = useState<Plan_Type_Enum | null>(null);

  const {
    isMonthlySubscriber,
    isYearlySubscriber,
    isLifetimeUser,
    isTerminated,
    plan,
    rechargeTokens,
    nextTokenRecycleTime,
    isLegacy,
  } = useCurrentUser();

  // Active subscriber = has a paid plan AND is not terminated
  const isActiveSubscriber = (isMonthlySubscriber || isYearlySubscriber || isLifetimeUser) && !isTerminated;

  // Terminated with a prior paid plan (needs to re-subscribe)
  const isTerminatedWithPlan = isTerminated && plan !== Plan_Type_Enum.Free;

  const { isTrialEligible } = useCheckTrialEligibilityQuery();

  useEffect(() => {
    if ((data?.discount || 0) > 0) {
      setIsPPPActive(true);
    }
  }, [data]);

  if (isPending || !data) {
    return <PageLoader shift />;
  }

  const handleContinue = async (planType: Plan_Type_Enum) => {
    const countMap: Record<string, number> = {
      [Plan_Type_Enum.Monthly]: 1,
      [Plan_Type_Enum.Yearly]: 5,
      [Plan_Type_Enum.Lifetime]: 10,
    };
    mutation.mutate({ count: countMap[planType] || 1 });

    setProcessingPlan(planType);

    const { data: subscribeData, error } = await supabase.functions.invoke<string>(
      EdgeFunctionName.Subscribe,
      {
        body: {
          plan: planType,
          isPPP: isPPPActive,
          ...getMetaTrackingData(),
        } as SubscribeParams,
      }
    );

    if (error || !subscribeData) {
      toastError(error?.message || 'Something went wrong');
      setProcessingPlan(null);
      return;
    }

    window.location.replace(subscribeData);
  };

  // For terminated users: only show their previous plan
  const getAvailablePlans = (): Plan_Type_Enum[] | undefined => {
    if (!isTerminatedWithPlan) return undefined; // all plans
    if (plan === Plan_Type_Enum.Monthly) return [Plan_Type_Enum.Monthly];
    if (plan === Plan_Type_Enum.Yearly) return [Plan_Type_Enum.Yearly];
    return undefined;
  };

  const renderSubscriberInfo = () => (
    <VStack width="100%" spacing={3}>
      <Stack mt={8} mb={2} direction={['column', 'column', 'row', 'row']}>
        <Text fontSize="xxxLarge" fontWeight="semibold" textAlign="center">
          You are currently on a {` `}
        </Text>
        <Text
          fontSize="xxxLarge"
          color={plans[plan].color}
          fontWeight="semibold"
          opacity="default"
          textAlign="center"
        >
          {plan} Plan
        </Text>
      </Stack>
      <HStack spacing={1}>
        <Text fontWeight="regular">which recharges</Text>
        <BigTokens amount={rechargeTokens} />
        <Text fontWeight="semibold">BigTokens monthly.</Text>
      </HStack>
      <HStack spacing={1}>
        <Text fontWeight="regular">Next recharge date:</Text>
        <Text fontWeight="semibold">{format(new Date(nextTokenRecycleTime), DATE_FORMAT)}.</Text>
      </HStack>
      <Text fontWeight="regular">
        Manage your subscription in the <Link to={ERoute.Billing}>Billing page</Link>.
      </Text>
      <Button
        mt={2}
        scheme="gradient"
        onClick={onOpen}
        leftIcon={<Icon type="Bigtokens" size={24} />}
      >
        Get BigTokens now
      </Button>
      <TokensModal isOpen={isOpen} onClose={onClose} />
    </VStack>
  );

  return (
    <Container maxW="1100px">
      <SEO
        title="BigDevSoon | Plans"
        description={`Choose the best plan that fits your needs.`}
        url={`${process.env.REACT_APP_SITE_URL}plans`}
      />
      <VStack width="100%" spacing={10} py={8}>
        {isActiveSubscriber ? (
          renderSubscriberInfo()
        ) : (
          <>
            {/* Header */}
            <VStack spacing={3}>
              <Text fontSize="xxxxLarge" fontWeight="bold" textAlign="center">
                Simple, transparent pricing
              </Text>
              <Text fontSize="large" opacity="secondary" textAlign="center">
                {isTrialEligible
                  ? 'Start with a 7-day free trial. Cancel anytime.'
                  : isTerminatedWithPlan
                    ? 'Welcome back! Re-activate your subscription to continue.'
                    : 'Choose a plan that fits your needs.'}
              </Text>
            </VStack>

            {/* 2-column checkout */}
            <PlanCheckout
              priceData={data}
              isPriceLoading={false}
              isTrialEligible={isTrialEligible}
              onSubscribe={handleContinue}
              processingPlan={processingPlan}
              availablePlans={getAvailablePlans()}
              defaultPlan={isTerminatedWithPlan ? (plan as Plan_Type_Enum) : undefined}
              showContactMessage={isTerminatedWithPlan}
              isPPPActive={isPPPActive}
              setIsPPPActive={setIsPPPActive}
            />
          </>
        )}
        <FeaturesList />
        <FAQ isLegacy={isLegacy} />
      </VStack>
    </Container>
  );
};

export default Pricing;
